import { HighlightOff, Login, Logout } from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { MRT_Row } from "material-react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Axios from "axios";
import type { LocationInventory } from "../../FloorManager/typings/location-inventory";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { useRemoteVisitOnlyVisitActions } from "../../../hooks/Remote/Schedule/useRemoteVisitOnlyVisitActions";
import { getCheckinErrorTranslationKey } from "../../Checkin/functions/checkin";
import { resolveVisitorReceptionTarget } from "./visitor-reception-api.logic";
import { shouldConfirmCheckInWithoutConsent } from "./visitor-bookings-consent.logic";
import { useRemotePatchActiveBookingRequests } from "../../../hooks/Remote/BookingRequests/useRemotePatchActiveBookingRequests";
import {
  getVisitorBookingRegistrationActionVisibility,
  isVisitorBookingAwaitingApproval
} from "./visitor-bookings-registration-actions.logic";
import {
  buildBookingApprovalRejectPayloadForVisit,
  canManageRequestedVisitorBooking
} from "./visitor-bookings-request-admin.logic";
import { VisitorBookingsCheckInWithoutConsentDialog } from "./visitor-bookings-check-in-without-consent-dialog.component";
import { VisitorBookingsRejectVisitDialog } from "./visitor-bookings-reject-visit-dialog.component";
import {
  getVisitorBookingVisitActionSnackbarKeys,
  type VisitorBookingVisitAction
} from "./visitor-bookings-visit-action-snackbar.logic";

type P = {
  row: MRT_Row<BookingScheduleInterface>;
  onDone: () => void;
  canManageRequestedAtLocation: (locationInventoryId: number | null | undefined) => boolean;
  locations: LocationInventory[] | undefined;
};

export function VisitorBookingsActionsCell({
  row,
  onDone,
  canManageRequestedAtLocation,
  locations
}: P) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { checkInReception, checkOutReception, rejectReception } = useRemoteVisitOnlyVisitActions();
  const { mutateAsync: patchBookingRequestStatus } = useRemotePatchActiveBookingRequests();
  const visit = row.original;
  const canManageRequestedBookings = canManageRequestedVisitorBooking(
    visit,
    canManageRequestedAtLocation,
    locations
  );
  const adminContext = useMemo(
    () => ({ canManageRequestedBookings }),
    [canManageRequestedBookings]
  );
  const id = visit.id;
  const receptionTarget = useMemo(() => resolveVisitorReceptionTarget(visit), [visit]);

  const checkInLabel = t("_visitorBookings_checkIn");
  const checkOutLabel = t("_visitorBookings_checkOut");
  const rejectLabel = t("_visitorBookings_rejectVisit");

  const [checkInWithoutConsentOpen, setCheckInWithoutConsentOpen] = useState(false);
  const [rejectVisitOpen, setRejectVisitOpen] = useState(false);

  const needsCheckInWithoutConsentConfirm = useMemo(
    () => shouldConfirmCheckInWithoutConsent(visit),
    [visit]
  );

  const { showCheckIn, showCheckOut, canReject, showCheckedInStatus, showAwaitingApprovalStatus } =
    useMemo(
      () => getVisitorBookingRegistrationActionVisibility(visit, adminContext),
      [visit, adminContext]
    );

  const checkedInLabel = t("_visitorBookings_checkedIn");
  const awaitingApprovalLabel = t("_visitorBookings_awaitingApproval");

  const handleVisitMutation = async (
    action: VisitorBookingVisitAction,
    mutation: Promise<unknown>
  ) => {
    const snackbarKeys = getVisitorBookingVisitActionSnackbarKeys(action);
    try {
      await mutation;
      enqueueSnackbar(t(snackbarKeys.success), { variant: "success" });
      onDone();
    } catch (e) {
      if (Axios.isAxiosError(e) && e.response?.status === 404) {
        enqueueSnackbar(t(snackbarKeys.notAvailable), { variant: "warning" });
        return;
      }

      if (action === "checkIn" && Axios.isAxiosError(e)) {
        const checkinErrorKey = getCheckinErrorTranslationKey(
          typeof e.response?.data?.message === "string" ? e.response.data.message : undefined
        );
        if (checkinErrorKey) {
          enqueueSnackbar(t(checkinErrorKey), { variant: "error" });
          return;
        }
      }

      enqueueSnackbar(t(snackbarKeys.failed), { variant: "error" });
    }
  };

  const performCheckIn = async () => {
    if (!receptionTarget) return;
    await handleVisitMutation("checkIn", checkInReception.mutateAsync(receptionTarget));
  };

  const onCheckInClick = async () => {
    if (needsCheckInWithoutConsentConfirm) {
      setCheckInWithoutConsentOpen(true);
      return;
    }
    await performCheckIn();
  };

  const onConfirmCheckInWithoutConsent = async () => {
    setCheckInWithoutConsentOpen(false);
    await performCheckIn();
  };
  const onCheckOut = async () => {
    if (!receptionTarget) return;
    await handleVisitMutation("checkOut", checkOutReception.mutateAsync(receptionTarget));
  };

  const onConfirmRejectVisit = async (reason: string) => {
    setRejectVisitOpen(false);

    if (canManageRequestedBookings && isVisitorBookingAwaitingApproval(visit)) {
      const payload = buildBookingApprovalRejectPayloadForVisit(visit, reason);
      if (!payload) {
        return;
      }
      await handleVisitMutation("reject", patchBookingRequestStatus(payload));
      return;
    }

    if (!receptionTarget) {
      return;
    }
    await handleVisitMutation(
      "reject",
      rejectReception.mutateAsync({
        target: receptionTarget,
        ...(reason.length > 0 ? { reason } : {})
      })
    );
  };

  return (
    <>
      <Box
        sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}
        data-testid={`visitor-booking-actions-${id}`}
      >
        {showCheckedInStatus && (
          <Chip
            label={checkedInLabel}
            color="success"
            size="small"
            variant="outlined"
            data-testid={`visitor-booking-checked-in-${id}`}
          />
        )}

        {showAwaitingApprovalStatus && (
          <Chip
            label={awaitingApprovalLabel}
            color="warning"
            size="small"
            variant="outlined"
            data-testid={`visitor-booking-requested-${id}`}
          />
        )}

        {canReject && (
          <Tooltip title={<Typography>{rejectLabel}</Typography>}>
            <span>
              <IconButton
                aria-label={rejectLabel}
                onClick={() => setRejectVisitOpen(true)}
                color="error"
                data-testid={`visitor-booking-reject-${id}`}
              >
                <HighlightOff />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Check-in */}
        {showCheckIn && (
          <Tooltip title={<Typography>{checkInLabel}</Typography>}>
            <span>
              <IconButton
                aria-label={checkInLabel}
                onClick={onCheckInClick}
                data-testid={`visitor-booking-check-in-${id}`}
                color="primary"
              >
                <Login />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Check-out */}
        {showCheckOut && (
          <Tooltip title={<Typography>{checkOutLabel}</Typography>}>
            <span>
              <IconButton
                aria-label={checkOutLabel}
                onClick={onCheckOut}
                data-testid={`visitor-booking-check-out-${id}`}
                color="primary"
              >
                <Logout />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <VisitorBookingsCheckInWithoutConsentDialog
        open={checkInWithoutConsentOpen}
        onClose={() => setCheckInWithoutConsentOpen(false)}
        onConfirm={onConfirmCheckInWithoutConsent}
      />
      <VisitorBookingsRejectVisitDialog
        open={rejectVisitOpen}
        onClose={() => setRejectVisitOpen(false)}
        onConfirm={onConfirmRejectVisit}
      />
    </>
  );
}
